import {
  FilterCheckbox,
  FilterDateRange,
  FilterType,
  SoDataTableColumnType,
  SoDataTableHeader,
} from "@/types/components/SoDataTable"
import { IComplaint } from "@/models/complaint/complaint.types"
import { Complaint as ComplaintRaw } from "@/api/hasura/generated/graphql"
import { Dayjs } from "dayjs"
import { IComplaintSummary } from "@/models/operations/complaintSummary/complaintSummary.types"
import {
  COMPLAINT_DEFAULT_STATUS_FILTER_OPTIONS,
  COMPLAINT_DEFAULT_TYPE_FILTER_OPTIONS,
  COMPLAINT_OPEN_STATUSES,
} from "@/constants/complaints"
import {
  ComplaintColumnValues,
  IComplaintColumn,
} from "@/models/operations/complaintColumn/complaintColumn.types"
import { SoRadioCardChartType } from "@/types/components/SoRadioCardChart"

const complaintsFundamentalsHeaders: SoDataTableHeader<
  keyof IComplaint,
  keyof ComplaintRaw
>[] = [
  {
    title: "Case / Ticket ID",
    key: "externalIdentifierPath",
    sortKey: "external_identifier",
    type: SoDataTableColumnType.EXTERNAL_LINK,
  },
  {
    title: "Date Raised",
    key: "dateRaised",
    sortKey: "date_raised",
    type: SoDataTableColumnType.DATE,
  },
  { title: "Owner", key: "agentName", sortKey: "agent_name" },
  {
    title: "Customer Id",
    key: "accountPath",
    sortable: false,
    type: SoDataTableColumnType.EXTERNAL_LINK,
  },
  {
    title: "Customer preferred contact method",
    key: "complaintsCustPrefContactMeth",
    sortable: false,
  },
  { title: "Status", key: "statusText", sortKey: "status" },
  { title: "Sub Status", key: "subStatus", sortKey: "sub_status" },
  {
    title: "Goodwill Amount",
    key: "complaintsGoodwillAmount",
    sortKey: "complaints_goodwill_amount",
  },
]

const communicationsHeaders: SoDataTableHeader<
  keyof IComplaint,
  keyof ComplaintRaw
>[] = [
  {
    title: "Day 1 comms sent",
    key: "d1SignpostingCommsSent",
    sortKey: "d1_signposting_comms_sent",
    type: SoDataTableColumnType.YES_OR_NO,
  },
  {
    title: "Day 1 comms sent at",
    key: "sendDateOfD1SignpostingComms",
    sortKey: "send_date_of_d1_signposting_comms",
    type: SoDataTableColumnType.DATE_TIME,
  },
  {
    title: "Day 56 comms sent",
    key: "d56SignpostingCommsSent",
    sortKey: "d56_signposting_comms_sent",
    sortable: false,
    type: SoDataTableColumnType.YES_OR_NO,
  },
  {
    title: "Day 56 comms sent at",
    key: "sendDateOfD56SignpostingComms",
    sortKey: "send_date_of_d56_signposting_comms",
    type: SoDataTableColumnType.DATE_TIME,
  },
  {
    title: "Deadlock Comms Sent",
    key: "deadlockCommsSent",
    sortKey: "deadlock_comms_sent",
    type: SoDataTableColumnType.YES_OR_NO,
  },
]

const complaintReasonHeaders: SoDataTableHeader<
  keyof IComplaint,
  keyof ComplaintRaw
>[] = [
  { title: "Category 1", key: "category1", sortKey: "category1" },
  { title: "Category 2", key: "category2", sortKey: "category2" },
  { title: "Category 3", key: "category3", sortKey: "category3" },
  {
    title: "Ofgem Complaint Category",
    key: "ofgemMappedComplaintCategory",
    sortKey: "ofgem_mapped_complaint_category",
  },
]

const resolutionMetricsHeaders: SoDataTableHeader<
  keyof IComplaint,
  keyof ComplaintRaw
>[] = [
  { title: "Age", key: "age", sortable: false },
  { title: "Age profile", key: "ageProfile", sortable: false },
  {
    title: "Closed at",
    key: "closedAt",
    sortKey: "closed_at",
    type: SoDataTableColumnType.DATE_TIME,
  },
]

const customerContactHeaders: SoDataTableHeader<
  keyof IComplaint,
  keyof ComplaintRaw
>[] = [
  { title: "Team", key: "team", sortKey: "team" },
  { title: "Type", key: "type", sortKey: "type" },
  { title: "Channel", key: "channel", sortKey: "channel" },
]

export const allHeaders: SoDataTableHeader<
  keyof IComplaint,
  keyof ComplaintRaw
>[] = [
  { title: "Internal id", key: "id", sortKey: "id", isVisible: false },
  ...complaintsFundamentalsHeaders,
  ...communicationsHeaders,
  ...complaintReasonHeaders,
  ...resolutionMetricsHeaders,
  ...customerContactHeaders,

  { title: "Agent Email", key: "agentEmail", sortKey: "agent_email" },
  { title: "Ombudsman Case", key: "ombudsmanCase", sortKey: "ombudsman_case" },
  {
    title: "Internal Complaint Category",
    key: "internalComplaintCategory",
    sortKey: "internal_complaint_category",
  },
  { title: "Complaint Id", key: "complaintId", sortable: false },
  { title: "Contact Email", key: "contactEmail", sortable: false },
  { title: "Billing Account Ids", key: "billingAccountIds", sortable: false },
]

export const defaultHeaderKeys: (keyof IComplaint)[] = [
  "id",
  "externalIdentifierPath",
  "dateRaised",
  "agentName",
  "accountPath",
  "complaintsCustPrefContactMeth",
  "channel",
  "statusText",
  "subStatus",
  "complaintsGoodwillAmount",
]

export type TableFilters = {
  status: FilterCheckbox
  subStatus: FilterCheckbox
  dateRaised: FilterDateRange
  ageProfile: FilterCheckbox
  type: FilterCheckbox
  channel: FilterCheckbox
  team: FilterCheckbox
  agentName: FilterCheckbox
  category1: FilterCheckbox
  category2: FilterCheckbox
  category3: FilterCheckbox
  ofgemCategory: FilterCheckbox
  internalCategory: FilterCheckbox
}

export interface ComplaintFilterTypes {
  dateRaisedFrom?: Dayjs
  dateRaisedTo?: Dayjs
  status?: string[]
  channel?: string[]
  type?: string[]
  subStatus?: string[]
  category1?: string[]
  category2?: string[]
  category3?: string[]
  agentName?: string[]
  team?: string[]
  internalCategory?: string[]
  ofgemCategory?: string[]
  ageProfile?: string[]
}

export const tableFilters: TableFilters = {
  status: {
    type: FilterType.CHECKBOX,
    label: "Status",
    options: {},
    value: COMPLAINT_OPEN_STATUSES,
    expandable: true,
  },
  subStatus: {
    type: FilterType.CHECKBOX,
    label: "Sub-status",
    options: {},
    value: [],
    expandable: true,
  },
  dateRaised: {
    type: FilterType.DATE_RANGE,
    label: "Date raised",
    value: { from: null, to: null },
    expandable: true,
  },
  ageProfile: {
    type: FilterType.CHECKBOX,
    label: "Age profile",
    options: {
      0: "0-1 weeks",
      1: "1-2 weeks",
      2: "2-3 weeks",
      3: "3-4 weeks",
      4: "4-5 weeks",
      5: "5-6 weeks",
      6: "6-7 weeks",
      7: "7-8 weeks",
      8: "8+ weeks",
    },
    value: [],
    expandable: true,
  },
  type: {
    type: FilterType.CHECKBOX,
    label: "Type",
    options: {},
    value: [],
    expandable: true,
  },
  channel: {
    type: FilterType.CHECKBOX,
    label: "Channel",
    options: {},
    value: [],
    expandable: true,
  },
  team: {
    type: FilterType.CHECKBOX,
    label: "Team",
    options: {},
    value: [],
    expandable: true,
  },
  agentName: {
    type: FilterType.CHECKBOX,
    label: "Agent",
    options: {},
    value: [],
    expandable: true,
  },
  category1: {
    type: FilterType.CHECKBOX,
    label: "Category 1",
    options: {},
    value: [],
    expandable: true,
  },
  category2: {
    type: FilterType.CHECKBOX,
    label: "Category 2",
    options: {},
    value: [],
    expandable: true,
  },
  category3: {
    type: FilterType.CHECKBOX,
    label: "Category 3",
    options: {},
    value: [],
    expandable: true,
  },
  ofgemCategory: {
    type: FilterType.CHECKBOX,
    label: "Ofgem category",
    options: {},
    value: [],
    expandable: true,
  },
  internalCategory: {
    type: FilterType.CHECKBOX,
    label: "Internal category",
    options: {},
    value: [],
    expandable: true,
  },
}

/**
 * Assigns default options to given filters as they might be missing from the column values query
 * @param filterName name of filter
 * @param options current options available in the database
 */
export function prepareOptionsForFilter(
  filterName: keyof IComplaintColumn,
  options: ComplaintColumnValues,
) {
  if (filterName === "status") {
    return { ...COMPLAINT_DEFAULT_STATUS_FILTER_OPTIONS, ...options }
  }
  if (filterName === "type") {
    return { ...COMPLAINT_DEFAULT_TYPE_FILTER_OPTIONS, ...options }
  }

  return options
}

export type TopFilter<
  Key extends keyof IComplaintSummary = keyof IComplaintSummary,
> = {
  name: Key
  label: string
  type: SoRadioCardChartType
  percentage?: number
  count?: number
}
export const topFilterOptions = [
  {
    name: "open",
    label: "Open complaints",
    type: "caution",
  },
  null, // used to easily generate an empty space in the filter placement
  {
    name: "newerThan5Weeks",
    label: "0-5 weeks",
    type: "normal",
  },

  {
    name: "olderThan5Weeks",
    label: "5+ weeks",
    type: "critical",
  },
  {
    name: "ombudsman",
    label: "Ombudsman",
    type: "critical",
  },
  {
    name: "cab",
    label: "CAB",
    type: "neutral",
  },
  {
    name: "ehu",
    label: "EHU",
    type: "neutral",
  },
  {
    name: "exec",
    label: "Exec",
    type: "neutral",
  },
] as const satisfies readonly (TopFilter | null)[]

/**
 * Defines link between topFilters and tableFilters
 */
export const topFiltersToTableFilters = {
  open: {
    filterPath: "status",
    filterValues: COMPLAINT_OPEN_STATUSES,
  },
  cab: {
    filterPath: "type",
    filterValues: ["CAB"],
  },
  ehu: {
    filterPath: "type",
    filterValues: ["EHU"],
  },
  exec: {
    filterPath: "type",
    filterValues: ["Exec"],
  },
  ombudsman: {
    filterPath: "type",
    filterValues: ["OS"],
  },
  newerThan5Weeks: {
    filterPath: "ageProfile",
    filterValues: ["0", "1", "2", "3", "4"],
  },
  olderThan5Weeks: {
    filterPath: "ageProfile",
    filterValues: ["5", "6", "7", "8"],
  },
} satisfies Record<
  keyof Omit<IComplaintSummary, "total">,
  { filterPath: keyof IComplaint; filterValues: string[] }
>

export const columnSelectorConfig = [
  {
    title: "Complaints Fundamentals",
    headers: complaintsFundamentalsHeaders,
  },
  {
    title: "Communications",
    headers: communicationsHeaders,
  },
  {
    title: "Complaint Reason",
    headers: complaintReasonHeaders,
  },
  {
    title: "Resolution Metrics",
    headers: resolutionMetricsHeaders,
  },
  {
    title: "Customer Contact",
    headers: customerContactHeaders,
  },
]
